import { EditOutlined, EyeOutlined } from '@ant-design/icons';
import { Pagination } from '@app/models/pagination.model';
import { CustomerTableRow, CustomerTableData, CustomerTableDTO } from '@app/models/customer/customers.table.dto';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { BaseTable } from '@app/components/common/BaseTable/BaseTable';
import { BaseButtonsForm } from '@app/components/common/forms/BaseButtonsForm/BaseButtonsForm';
import { useMounted } from '@app/hooks/useMounted';
import { Space, Tooltip, Button, Popconfirm, Input } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { getCustomerTableData } from '@app/api/customer.api';
import { notificationController } from '@app/controllers/notificationController';
import FilterComponent from '@app/components/TableFilter/TableFilter';

const initialPagination: Pagination = {
  current: 1,
  pageSize: 10,
};

interface CustomerTableProps {
  verified: boolean | null;
}

export const CustomerTable: React.FC<CustomerTableProps> = ({ verified = null }) => {
  const [statusOptions, setStatusOptions] = useState<Record<string, { label: string; value: string }[]> | null>(null);
  const [tableData, setTableData] = useState<CustomerTableDTO>({
    data: [],
    pagination: initialPagination,
    loading: false,
  });

  const { t } = useTranslation();
  const { isMounted } = useMounted();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<{ [key: string]: string | [string, string] | null }>({});
  const [form] = BaseButtonsForm.useForm();
  const lastFetchedRef = useRef(false);
  const [customerType, setCustomerType] = useState<string | null>(localStorage.getItem('selected_customer_type'));

  const fetch = useCallback(
    (pagination: Pagination) => {
      setTableData((tableData) => ({ ...tableData, loading: true }));
      getCustomerTableData({
        ...filters,
        ...pagination,
        verified,
        customer_type: customerType || form.getFieldValue('customer_type'),
      })
        .then((res: CustomerTableData) => {
          if (isMounted.current) {
            setTableData({
              data: res.data,
              pagination: {
                current: res.pagination.current,
                pageSize: res.pagination.pageSize,
                total: res.pagination.total,
              },
              customer_type_options: tableData.customer_type_options || res.customer_type_options,
              loading: false,
            });
            setStatusOptions(res.status_options);
          }
        })
        .catch((error: any) => {
          notificationController.error({
            message: error.message,
          });
        });
    },
    [form, verified, isMounted, filters, customerType],
  );

  useEffect(() => {
    fetch(initialPagination);
  }, [customerType, filters]);

  useEffect(() => {
    const newCustomerType = localStorage.getItem('selected_customer_type');
    if (newCustomerType !== customerType) {
      setCustomerType(newCustomerType);
      lastFetchedRef.current = false;
      fetch(initialPagination);
    }
  }, [localStorage.getItem('selected_customer_type')]);

  const handleTableChange = (pagination: Pagination) => {
    fetch({ ...filters, ...pagination });
  };

  const handleViewRow = (id: number) => {
    navigate('/view-customer', { state: { id, verified } });
  };

  const handleEditRow = (id: number) => {
    navigate('/edit-customer', { state: { id, verified } });
  };

  const showTotal = (total: number, range: [number, number]): React.ReactNode => {
    return (
      <span
        style={{ fontSize: '14px', marginRight: '20px', color: '#adadad' }}
      >{`Showing ${range[0]} - ${range[1]} of ${total} Records`}</span>
    );
  };

  const searchData: {
    name: string;
    placeholder: string;
    type: 'text' | 'dropdown';
    options?: Record<string, { label: string; value: string }[]>;
  }[] = [
    { name: 'search', placeholder: 'Search', type: 'text' },
    {
      name: 'status',
      placeholder: 'status',
      type: 'dropdown',
      options: statusOptions || { statusOptions: [] },
    },
  ];

  const columns: ColumnsType<CustomerTableRow> = [
    {
      title: '#',
      dataIndex: 'row-count',
      key: 'row-count',
      width: '5%',
      render: (_: any, __: any, index: number) => {
        const currentPage = tableData.pagination.current || 1;
        const pageSize = tableData.pagination.pageSize || 1;
        const rowNumber = (currentPage - 1) * pageSize + index + 1;

        return <Space>{rowNumber}</Space>;
      },
    },
    {
      title: 'First Name',
      dataIndex: 'first_name',
      key: 'first_name',
      width: '15%',
    },
    {
      title: 'Last Name',
      dataIndex: 'last_name',
      key: 'last_name',
      width: '15%',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: '15%',
    },
    {
      title: 'Contact No',
      dataIndex: 'contact_no',
      key: 'contact_no',
      width: '15%',
    },
    {
      title: 'Type',
      key: 'customer_type',
      dataIndex: 'customer_type',
      width: '15%',
      render: (_: any, record: CustomerTableRow) => {
        const typeLabel =
          tableData.customer_type_options?.find((option) => option.value === record.customer_type)?.label ||
          record.customer_type;

        return <span>{typeLabel}</span>;
      },
    },
    {
      title: 'Status',
      key: 'status',
      dataIndex: 'status',
      width: '15%',
      render: (_: any, record: CustomerTableRow) => {
        const typeLabel =
          tableData.status_options?.find((option: { value: string }) => option.value === record.status)?.label ||
          record.status;

        return <span>{typeLabel}</span>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '15%',
      render: (_, record) => {
        return (
          <Space>
            <Tooltip title="View">
              <Button
                type="text"
                onClick={() => handleViewRow(record.id)}
                icon={<EyeOutlined />}
                style={{ color: '#1890ff' }}
              />
            </Tooltip>

            <Tooltip title="Edit">
              <Button
                type="text"
                onClick={() => handleEditRow(record.id)}
                icon={<EditOutlined />}
                style={{ color: '#52c41a' }}
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ];
  return (
    <BaseCard
      id="basic-table"
      title={customerType == null ? 'Customers' : customerType === 'VERIFIED' ? 'Verified Customers' : 'New Customers'}
      padding="1.25rem"
      // extra={
      //   <BaseButtonsForm
      //     form={form}
      //     layout="inline"
      //     isFieldsChanged={fieldsChanged}
      //     setFieldsChanged={setFieldsChanged}
      //   >
      //     <BaseButtonsForm.Item name={'search'}>
      //       <Input
      //         size="small"
      //         suffix={
      //           <Button
      //             icon={<SearchOutlined />}
      //             size="small"
      //             type="text"
      //             //   onClick={() => {
      //             //     fetch(initialPagination);
      //             //   }}
      //           />
      //         }
      //         placeholder={'Search Product Varieties'}
      //       />
      //     </BaseButtonsForm.Item>
      //     <Button
      //       type="ghost"
      //       size="small"
      //       icon={<ReloadOutlined />}
      //       // onClick={() => {
      //       //   form.resetFields(['search']);
      //       //   fetch(initialPagination);
      //       // }}
      //     />
      //   </BaseButtonsForm>
      // }
    >
      <FilterComponent data={searchData} onFilter={setFilters} />
      <BaseTable
        columns={columns}
        dataSource={tableData.data}
        pagination={{
          current: tableData.pagination.current,
          pageSize: tableData.pagination.pageSize,
          total: tableData.pagination.total,
          showTotal,
        }}
        loading={tableData.loading}
        onChange={handleTableChange}
        scroll={{ x: true, scrollToFirstRowOnChange: true }}
      />
    </BaseCard>
  );
};
